import React, { useMemo, memo, useCallback } from 'react';
import { connect } from 'react-redux';
import ResponsiveDrawer, { desktopVariantType } from './ResponsiveDrawer';
import { drawers } from '../../../state/ducks/header/header';
import NotificationsPageConnected from '../../pages/Notifications/NotificationsPageConnected';
import { headerSelectors } from '../../../state/ducks/header/selectors';
import { RootState } from '../../../state/store/store';
import TasksStripConnected from '../../pages/Dashboard/Tasks/TasksStripConnected';
import ShipmentPaneConnected from '../ShipmentPane/ShipmentPaneConnected';
import ChatPaneConnected from '../../pages/Chat/ChatPaneConnected';
import { featureFlagsModel } from '../../../models/entities/businessPartner/settings';
import { userSelectors } from '../../../state/ducks/user/selectors';
import ChatRequestsConnected from '../../pages/Chat/ChatRequestsConnected';
import FollowingTabs from '../../pages/Following/FollowingTabs';
import QuotationPane from '../../pages/singleShipment/Quotation/QuotationPane';
import TariffQuotationPane from '../../pages/singleShipment/sections/TariffQuotations/TariffQuotationPane';
import AddEditUserPaneConnected from '../../pages/Admin/Users/AddEditUser/AddEditUserPaneConnected';
import AddEditCompanyPaneConnected from '../../pages/Admin/Companies/AddEditCompany/AddEditCompanyPaneConnected';
import ProductView from '../../pages/products/components/ProductView';
import InventoryFormConnected from '../../pages/inventory/components/Form/InventoryFormConnected';

type Props = {
    setDrawerOpen: (open: drawers) => void;
    isEmptyStateDrawer: boolean;
    drawerOpen: drawers;
    drawerParams: any;
    isSideBarOpen: boolean;
    setSideBarOpen: (open: boolean) => void;
    printMode?: boolean;
    featureFlags?: featureFlagsModel;
};
const specialConfiguration: { [key: string]: { defaultDrawerWidth?: number; drawerVariant?: desktopVariantType } } = {
    NOTIFICATION: {
        drawerVariant: 'temporary',
        defaultDrawerWidth: 420
    },
    QUOTATION: {
        drawerVariant: 'temporary',
        defaultDrawerWidth: 710
    },
    ADD_EDIT_USER: {
        defaultDrawerWidth: 710
    },
    ADD_EDIT_COMPANY: {
        defaultDrawerWidth: 710
    },
    PRODUCT: {
        drawerVariant: 'temporary',
        defaultDrawerWidth: 710
    },
    INVENTORY: {
        drawerVariant: 'temporary',
        defaultDrawerWidth: 710
    },
    FOLLOW: {
        drawerVariant: 'temporary',
        defaultDrawerWidth: 710
    },
    TASK: {
        drawerVariant: 'temporary',
        defaultDrawerWidth: 630
    },
    CHAT: {
        defaultDrawerWidth: 500
    },
    SHIPMENT: {
        drawerVariant: 'temporary',
        defaultDrawerWidth: 710
    }
};
const ResponsiveDrawerConnected = ({
    setDrawerOpen,
    isEmptyStateDrawer,
    drawerOpen,
    drawerParams,
    isSideBarOpen,
    setSideBarOpen,
    printMode,
    featureFlags
}: Props) => {
    // Compute the drawer type without dynamic parameters to avoid unnecessary remounts
    const drawerType = useMemo(() => {
        if (!drawerOpen) return null;

        // Extract the base drawer type without dynamic IDs
        if (drawerOpen.startsWith('SHIPMENT')) return 'SHIPMENT';
        if (drawerOpen.startsWith('CHAT-')) return 'CHAT';
        if (drawerOpen.startsWith('ADD_EDIT_USER')) return 'ADD_EDIT_USER';
        if (drawerOpen.startsWith('ADD_EDIT_COMPANY')) return 'ADD_EDIT_COMPANY';

        return drawerOpen;
    }, [drawerOpen]);

    // Create stable component instances that don't change on re-renders
    const stableComponents = useMemo(
        () => ({
            NOTIFICATION: <NotificationsPageConnected key="notifications-stable" />,
            TASK: <TasksStripConnected key="task-strip-stable" />,
            FOLLOW: <FollowingTabs key="follow-tabs-stable" />,
            CHAT_REQUESTS: <ChatRequestsConnected key="chat-requests-stable" />
        }),
        []
    );

    const drawerContent = useMemo(() => {
        // Use stable instances for static components, create new ones for dynamic components
        switch (drawerType) {
            case 'NOTIFICATION':
                return stableComponents.NOTIFICATION;
            case 'TASK':
                return stableComponents.TASK;
            case 'FOLLOW':
                return stableComponents.FOLLOW;
            case 'CHAT_REQUESTS':
                return stableComponents.CHAT_REQUESTS;
            case 'SHIPMENT':
                return <ShipmentPaneConnected params={drawerParams} key={`shipment-${drawerParams?.shipmentId}`} />;
            case 'CHAT':
                return <ChatPaneConnected params={drawerParams} key={`chat-${JSON.stringify(drawerParams)}`} />;
            case 'QUOTATION':
                return <QuotationPane params={drawerParams} key={`quotation-${JSON.stringify(drawerParams)}`} />;
            case 'TARIFF_QUOTATION':
                return <TariffQuotationPane params={drawerParams} key={`tariff-quotation-${JSON.stringify(drawerParams)}`} />;
            case 'ADD_EDIT_USER':
                return <AddEditUserPaneConnected params={drawerParams} key={`add-edit-user-${JSON.stringify(drawerParams)}`} />;
            case 'ADD_EDIT_COMPANY':
                return <AddEditCompanyPaneConnected params={drawerParams} key={`add-edit-company-${JSON.stringify(drawerParams)}`} />;
            case 'PRODUCT':
                return <ProductView params={drawerParams} key={`product-${JSON.stringify(drawerParams)}`} />;
            case 'INVENTORY':
                return <InventoryFormConnected mode={'NEW'} key="inventory-new" />;
            default:
                return <div></div>;
        }
    }, [drawerType, drawerParams, stableComponents]);

    const config = useMemo(() => {
        // Always return a stable default config to prevent prop changes
        const defaultConfig = { drawerVariant: 'persistent' as desktopVariantType, defaultDrawerWidth: 0 };

        if (!drawerOpen) return defaultConfig;

        const foundConfig = Object.entries(specialConfiguration).find(([key, _]) => drawerOpen.includes(key))?.[1];
        return foundConfig ? { ...defaultConfig, ...foundConfig } : defaultConfig;
    }, [drawerOpen]);
    return (
        <ResponsiveDrawer
            isOpen={drawerOpen !== null}
            setIsOpen={useCallback(() => setDrawerOpen(null), [setDrawerOpen])}
            drawerContent={drawerContent}
            isEmptyState={isEmptyStateDrawer}
            isSideBarOpen={isSideBarOpen}
            setSideBarOpen={useCallback((open: boolean) => setSideBarOpen(open), [setSideBarOpen])}
            printMode={printMode}
            hasChat={featureFlags?.CHAT}
            desktopVariant={config.drawerVariant}
            defaultDrawerWidth={config.defaultDrawerWidth}
            drawerType={drawerType}
        />
    );
};

const mapStateToProps = (state: RootState) => ({
    drawerOpen: headerSelectors.drawerOpen(state),
    drawerParams: headerSelectors.drawerParams(state),
    isEmptyStateDrawer: headerSelectors.isEmptyStateDrawer(state),
    isSideBarOpen: headerSelectors.sideBarOpen(state),
    printMode: headerSelectors.printMode(state),
    featureFlags: userSelectors.featureFlags(state)
});

const mapDispatchToProps = (dispatch: any) => ({
    setDrawerOpen: (open: drawers) => dispatch.header.setDrawerOpen(open),
    setSideBarOpen: (open: boolean) => dispatch.header.setSideBarOpen(open)
});

export default connect(mapStateToProps, mapDispatchToProps)(memo(ResponsiveDrawerConnected));
